<script setup lang="ts">
  import { ref, computed, onMounted, watch } from "vue";
  import type { Component } from "vue";
  import { storeToRefs } from "pinia";
  import OnePanePage from "@/templates/OnePanePage.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import ProjectSummaryTable from "@/components/Admin/Pricer/ProjectSummary/ProjectSummaryTable.vue";
  import ProjectSummaryModal from "@/components/Admin/Pricer/ProjectSummary/ProjectSummaryModal.vue";
  import ProjectSummaryHubSpotModal from "@/components/Admin/Pricer/ProjectSummary/ProjectSummaryHubSpotModal.vue";
  import ProjectSummaryQuoteModal from "@/components/Admin/Pricer/ProjectSummary/ProjectSummaryQuoteModal.vue";
  import { usePricerProjectStore } from "@/stores/pricer/pricerProject";
  import { getServiceFees, buildInitialFeeVisibility } from "@/utils/pricer/pricerHelpers";
  import { openAiHelperView, downloadTableAsImage } from "@/utils/pricer/pricerSummaryHelpers";
  import { hubspotDealUrl, hubspotQuoteUrl } from "@/utils/pricer/hubspotUrls";
  import { Wand2, Mail, Download, SquareArrowOutUpRight, FileText, ExternalLink } from "lucide-vue-next";
  import { Tooltip } from "@/components/ui/tooltip";
  import type { PricerProject } from "@/types";

  interface SummaryAction {
    label: string;
    icon: Component;
    action?: () => void;
    tooltip?: string;
    disabled?: boolean;
  }

  const { project: storedProject, hasProjectChanged } = storeToRefs(usePricerProjectStore());
  const project = computed<Partial<PricerProject>>(() => storedProject.value);

  const feeVisibility = ref<Record<string, boolean>>({});
  const serviceFees = computed(() => getServiceFees(storedProject.value, feeVisibility.value));
  const isServiceFeesModalOpen = ref(false);
  const isEditHubSpotModalOpen = ref(false);
  const isQuoteModalOpen = ref(false);
  const tableRef = ref<HTMLElement | null>(null);

  const resetFeeVisibility = () => (feeVisibility.value = buildInitialFeeVisibility(storedProject.value));
  const toggleServiceFeesModal = () => (isServiceFeesModalOpen.value = !isServiceFeesModalOpen.value);
  const toggleModalEditHubSpot = () => (isEditHubSpotModalOpen.value = !isEditHubSpotModalOpen.value);
  const toggleQuoteModal = () => (isQuoteModalOpen.value = !isQuoteModalOpen.value);

  const hubspotActionLabel = computed(() => (project.value.crm_id ? "Sync deal in HubSpot" : "Create deal in HubSpot"));

  const openHubSpotDeal = () => project.value.crm_id && window.open(hubspotDealUrl(project.value.crm_id), "_blank");
  const openHubSpotQuote = () =>
    project.value.hs_quote_id && window.open(hubspotQuoteUrl(project.value.hs_quote_id), "_blank");

  const quoteAction = computed<SummaryAction>(() => {
    const label = project.value.hs_quote_id ? "Update quote in HubSpot" : "Create quote in HubSpot";
    if (!project.value.crm_id) {
      return { label, tooltip: "Create the deal in HubSpot first", icon: FileText, disabled: true };
    }
    if (hasProjectChanged.value) {
      return { label, tooltip: "Save the project before generating the quote", icon: FileText, disabled: true };
    }
    return { label, icon: FileText, action: toggleQuoteModal };
  });

  const summaryActions = computed<SummaryAction[]>(() => {
    const actions: SummaryAction[] = [];

    if (project.value.crm_id) {
      actions.push({ label: "View deal in HubSpot", icon: SquareArrowOutUpRight, action: openHubSpotDeal });
    }

    actions.push({ label: hubspotActionLabel.value, icon: Wand2, action: toggleModalEditHubSpot });

    if (project.value.hs_quote_id) {
      actions.push({ label: "View quote in HubSpot", icon: ExternalLink, action: openHubSpotQuote });
    }

    actions.push(
      quoteAction.value,
      { label: "Draft email", icon: Mail, action: () => openAiHelperView(project.value.token) },
      {
        label: "Download table",
        icon: Download,
        action: () => downloadTableAsImage(tableRef.value, project.value.token),
      },
    );

    return actions;
  });

  onMounted(resetFeeVisibility);
  watch(
    () => project.value.token,
    (newToken, oldToken) => newToken && newToken !== oldToken && resetFeeVisibility(),
  );
</script>

<template>
  <OnePanePage :span-center="8">
    <Card class="shadow-sm border-border max-h-[calc(100vh-60px-40px)] flex flex-col">
      <CardHeader class="flex flex-row items-center justify-between space-y-0 text-left shrink-0 p-6 pb-2">
        <CardTitle class="heading-page">Project Summary</CardTitle>
        <div class="flex items-center gap-4">
          <Tooltip
            v-for="(action, index) in summaryActions"
            :key="index"
            :content="action.tooltip || action.label">
            <span class="inline-flex">
              <Button
                variant="ghost"
                size="icon-sm"
                :disabled="action.disabled || !action.action"
                @click="action.action">
                <component
                  :is="action.icon"
                  class="h-4 w-4" />
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outline"
            class="shadow-xs"
            @click="toggleServiceFeesModal">
            Edit service fees
          </Button>
        </div>
      </CardHeader>
      <CardContent class="overflow-y-auto flex-1 min-h-0 p-0">
        <div
          ref="tableRef"
          class="p-6 pt-0">
          <ProjectSummaryTable :service-fees="serviceFees" />
        </div>
      </CardContent>
    </Card>
    <ProjectSummaryModal
      v-if="project.token"
      v-model:open="isServiceFeesModalOpen"
      v-model:fee-visibility="feeVisibility"
      :service-fees="serviceFees" />
    <ProjectSummaryHubSpotModal
      v-if="project.token"
      v-model:open="isEditHubSpotModalOpen" />
    <ProjectSummaryQuoteModal
      v-if="project.token"
      v-model:open="isQuoteModalOpen" />
  </OnePanePage>
</template>
